import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Chip, InputAdornment, Modal, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReusableTable from '../../common/table/CommonTable';
import { deleteStaff, fetchStaff } from '../../redux/staff/staffAction';

const ListStaff = () => {
  const [loading, setLoading] = useState(false);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const staffState = useSelector((state) => state?.staffs?.fetchListStaff || {});
  const deleteStaffState = useSelector((state) => state?.staffs?.deleteStaff || {});
  
  const staffList = staffState?.data?.data || [];
  const totalPages = staffState?.data?.totalPages || 0;

  // Load staff data whenever page changes or after deletion
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await dispatch(fetchStaff(currentPage));
      } catch (error) {
        console.error("Error fetching staff data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentPage, dispatch, deleteStaffState.success]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Transform data and filter based on search query
  const filteredData = Array.isArray(staffList) 
    ? staffList
        .map((staff) => ({
          ids: staff.id || 'N/A',
          id: staff.staff_id || 'N/A',
          name: staff.staff_name || 'N/A',
          email: staff.email || 'N/A',
          phone: staff.phone || 'N/A',
          status: staff?.User?.status === 1 ? 'Active' : 'Inactive',
        }))
        .filter((staff) => {
          if (!searchQuery) return true;
          const query = searchQuery.toLowerCase();
          return (
            staff.id.toLowerCase().includes(query) ||
            staff.name.toLowerCase().includes(query) ||
            staff.email.toLowerCase().includes(query) ||
            staff.phone.toLowerCase().includes(query) ||
            staff.status.toLowerCase().includes(query)
          );
        })
    : [];

  const columns = [
     {
      id: "id",
      label: "Staff ID",
      sortable: true,
      render: (value, row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "dodgerblue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => handleViewClick(row)}
        >
          {value}
        </Typography>
      ),
    },
    // { id: 'id', label: 'Staff ID', sortable: true },
    { id: 'name', label: 'Name', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'phone', label: 'Phone Number', sortable: true },
    { 
      id: 'status', 
      label: 'Status', 
      sortable: true,
      render: (value) => (
        <Chip label={value} color={value === 'Active' ? 'success' : 'warning'} />
      )
    }
  ];

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    // Reset to first page when searching
    setCurrentPage(1);
  };

  const handleEditClick = (row) => {
    navigate(`/staff/edit/${row.ids}`);
  };

  const handleViewClick = (row) => {
    navigate(`/staff/view/${row.ids}`);
  };
  
  const handleDeleteClick = (row) => {
    setDeleteId(row.ids);
    setOpenDeleteModel(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await dispatch(deleteStaff(deleteId));
    } catch (error) {
      console.error("Error deleting staff:", error);
    } finally {
      setOpenDeleteModel(false);
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          placeholder="Search staff..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: '300px' }}
        />
      </Box>
      <ReusableTable
        title="Staffs"
        columns={columns}
        data={filteredData}
        isLoading={loading}
        headerColor="#000"
        rowColor="#ffffff"
        alternateRowColor="#f5f5f5"
        onViewClick={handleViewClick}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        withPagination={true}
        withActions={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showDownloadOption={true}
      />
      <Modal
        open={openDeleteModel}
        style={{ left: '35%', top: '35%' }}
        onClose={() => {
          setOpenDeleteModel(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ width: '500px', background: "#fff", padding: '20px' }}>
          <Typography id="modal-modal-title" variant="h5" sx={{ fontSize: 'large', fontWeight: '500' }} component="h2">
            Delete Staff
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Are you sure you want to delete this staff?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
            <Button variant="contained" onClick={handleDeleteConfirm}>OK</Button>
            <Button variant="contained" onClick={() => { setOpenDeleteModel(false); }}>Cancel</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ListStaff;